// Admin > Users & teams: access requests + invite links (backend
// routers/onboarding.py). Approve a request (organisation, role, teams,
// read-only; preset buttons fill them) or reject it; create a one-time
// invite link (shown once, copy it) and revoke open ones. Org admins see
// and act on their own organisation only; platform admins on any.
import { getJson, postJson } from "./api";
import { el } from "./legend";
import { teamPicker } from "./workspaces";
import type { Team } from "./state";

interface Preset {
  label: string;
  role: string;
  teams: string[];
  read_only: boolean;
}

interface RegRequest {
  id: number;
  username: string;
  display_name: string;
  email: string | null;
  organisation: string;
  reason: string | null;
  mfa: boolean;
  suggested_client_id: number | null;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  decided_at: string | null;
  username_taken: boolean;
}

interface Invite {
  id: number;
  client_name: string;
  role: string;
  teams: string[];
  read_only: boolean;
  label: string | null;
  email: string | null;
  created_at: string;
  expires_at: string;
  used_by: string | null;
  status: "open" | "used" | "revoked" | "expired";
  created_by: string | null;
}

interface Overview {
  requests: RegRequest[];
  invites: Invite[];
  presets: Record<string, Preset>;
  roles: string[];
  clients: { id: number; name: string }[];
  can_pick_client: boolean;
  default_client_id: number;
}

interface GrantControls {
  el: HTMLElement;
  value: () => { client_id: number | null; role: string; teams: string[]; read_only: boolean };
}

/** Preset buttons + organisation / role / teams / read-only controls. */
function grantControls(ov: Overview, defaultClient: number | null): GrantControls {
  const wrap = el("div", { className: "onb-admin-card-inner" });
  const presetRow = el("div", { className: "onb-admin-row" });
  const fieldsRow = el("div", { className: "onb-admin-row" });
  const teamsHolder = el("span");
  let picker = teamPicker(ov.presets.client_viewer?.teams ?? []);
  teamsHolder.append(picker.el);

  const orgSelect = el("select") as HTMLSelectElement;
  for (const c of ov.clients) orgSelect.append(el("option", { value: String(c.id) }, c.name) as HTMLOptionElement);
  orgSelect.value = String(defaultClient ?? ov.default_client_id);
  orgSelect.disabled = !ov.can_pick_client;
  const roleSelect = el("select") as HTMLSelectElement;
  for (const r of ov.roles) roleSelect.append(el("option", { value: r }, r) as HTMLOptionElement);
  const roBox = el("input", { type: "checkbox" }) as HTMLInputElement;
  const roLabel = el("label", { className: "caption", title: "Can look around but every change is refused" });
  roLabel.append(roBox, document.createTextNode(" Read-only"));

  const apply = (p: Preset) => {
    roleSelect.value = p.role;
    roBox.checked = p.read_only;
    picker = teamPicker(p.teams);
    teamsHolder.innerHTML = "";
    teamsHolder.append(picker.el);
  };
  for (const p of Object.values(ov.presets)) {
    const b = el("button", { type: "button", className: "secondary" }, p.label);
    b.addEventListener("click", () => apply(p));
    presetRow.append(b);
  }
  if (ov.presets.client_viewer) apply(ov.presets.client_viewer);

  fieldsRow.append(el("span", { className: "caption" }, "Organisation"), orgSelect, el("span", { className: "caption" }, "Role"), roleSelect, roLabel);
  const teamsRow = el("div", { className: "onb-admin-row" });
  teamsRow.append(el("span", { className: "caption" }, "Workspaces"), teamsHolder);
  wrap.append(presetRow, fieldsRow, teamsRow);
  return {
    el: wrap,
    value: () => ({
      client_id: ov.can_pick_client ? Number(orgSelect.value) : null,
      role: roleSelect.value,
      teams: picker.value() as Team[],
      read_only: roBox.checked,
    }),
  };
}

function requestCard(ov: Overview, r: RegRequest, refresh: () => void): HTMLElement {
  const card = el("div", { className: "onb-admin-card" });
  card.append(
    el("strong", {}, `${r.display_name} (${r.username})`),
    el("div", { className: "caption" }, `Organisation: ${r.organisation}${r.email ? ` · ${r.email}` : ""} · two-factor ${r.mfa ? "on" : "off"} · ${new Date(r.created_at).toLocaleString()}`),
  );
  if (r.reason) card.append(el("div", { className: "caption" }, `Reason: ${r.reason}`));
  const grant = grantControls(ov, r.suggested_client_id);
  card.append(grant.el);
  const userRow = el("div", { className: "onb-admin-row" });
  const uname = el("input", { type: "text", value: r.username }) as HTMLInputElement;
  uname.style.width = "12rem";
  userRow.append(el("span", { className: "caption" }, "Username"), uname);
  if (r.username_taken) userRow.append(el("span", { className: "caption login-error" }, "already taken: change it before approving"));
  card.append(userRow);
  const actions = el("div", { className: "onb-admin-row" });
  const msg = el("span", { className: "caption" });
  const approve = el("button", { type: "button" }, "Approve");
  const reject = el("button", { type: "button", className: "secondary" }, "Reject");
  approve.addEventListener("click", () => {
    approve.disabled = reject.disabled = true;
    postJson(`/api/admin/onboarding/requests/${r.id}/approve`, { ...grant.value(), username: uname.value.trim() })
      .then(() => refresh())
      .catch((e: Error) => {
        msg.textContent = e.message;
        approve.disabled = reject.disabled = false;
      });
  });
  reject.addEventListener("click", () => {
    const note = window.prompt(`Reject the request from ${r.display_name}? Optional note (kept in the audit log):`, "");
    if (note === null) return;
    approve.disabled = reject.disabled = true;
    postJson(`/api/admin/onboarding/requests/${r.id}/reject`, { note })
      .then(() => refresh())
      .catch((e: Error) => {
        msg.textContent = e.message;
        approve.disabled = reject.disabled = false;
      });
  });
  actions.append(approve, reject, msg);
  card.append(actions);
  return card;
}

function inviteForm(ov: Overview, refresh: () => void): HTMLElement {
  const card = el("div", { className: "onb-admin-card" });
  card.append(
    el("strong", {}, "Invite someone"),
    el("p", { className: "caption" }, "Creates a one-time link (valid 7 days). They pick their own username and password; you never see or send a password."),
  );
  const grant = grantControls(ov, null);
  const label = el("input", { type: "text", placeholder: "Who it's for (e.g. Jane, Tiaki Wai)" }) as HTMLInputElement;
  const email = el("input", { type: "email", placeholder: "Email (optional)" }) as HTMLInputElement;
  const row = el("div", { className: "onb-admin-row" });
  row.append(label, email);
  const out = el("div");
  const btn = el("button", { type: "button" }, "Create invite link");
  btn.addEventListener("click", () => {
    btn.disabled = true;
    out.innerHTML = "";
    postJson<{ path: string; expires_at: string }>("/api/admin/onboarding/invites", {
      ...grant.value(),
      label: label.value.trim() || null,
      email: email.value.trim() || null,
    })
      .then((res) => {
        const url = `${window.location.origin}${res.path}`;
        const linkBox = el("input", { type: "text", readOnly: true, value: url, className: "onb-link" }) as HTMLInputElement;
        const copy = el("button", { type: "button" }, "Copy link");
        copy.addEventListener("click", () => {
          linkBox.select();
          void navigator.clipboard?.writeText(url).then(() => (copy.textContent = "Copied"));
        });
        const r2 = el("div", { className: "onb-admin-row" });
        r2.append(linkBox, copy);
        out.append(r2, el("p", { className: "caption" }, `Shown once: copy it now and send it to them. Works once, expires ${new Date(res.expires_at).toLocaleString()}.`));
        refreshList();
      })
      .catch((e: Error) => out.append(el("p", { className: "caption login-error" }, e.message)))
      .finally(() => (btn.disabled = false));
  });
  // keep the newly shown link visible: refresh only the invite table
  const refreshList = () => document.dispatchEvent(new CustomEvent("onb-invites-changed"));
  void refresh;
  card.append(grant.el, row, btn, out);
  return card;
}

function inviteTable(ov: Overview, refresh: () => void): HTMLElement {
  const wrap = el("div");
  if (ov.invites.length === 0) {
    wrap.append(el("p", { className: "caption" }, "No invites yet."));
    return wrap;
  }
  const table = el("table", { className: "detail-table" });
  const head = document.createElement("tr");
  for (const h of ["For", "Organisation", "Role", "Workspaces", "Status", "Expires", "By", ""]) head.append(el("th", {}, h));
  table.append(head);
  for (const i of ov.invites) {
    const tr = document.createElement("tr");
    const status = i.status === "used" ? `used by ${i.used_by ?? "?"}` : i.status;
    tr.append(
      el("td", {}, [i.label, i.email].filter(Boolean).join(" · ") || "—"),
      el("td", { className: "caption" }, i.client_name),
      el("td", { className: "caption" }, `${i.role}${i.read_only ? " (read-only)" : ""}`),
      el("td", { className: "caption" }, i.teams.join(", ") || "—"),
      el("td", { className: "caption" }, status),
      el("td", { className: "caption" }, new Date(i.expires_at).toLocaleDateString()),
      el("td", { className: "caption" }, i.created_by ?? ""),
    );
    const td = el("td");
    if (i.status === "open") {
      const b = el("button", { type: "button", className: "secondary" }, "Revoke");
      b.addEventListener("click", () => {
        b.disabled = true;
        void postJson(`/api/admin/onboarding/invites/${i.id}/revoke`, {}).then(refresh, () => (b.disabled = false));
      });
      td.append(b);
    }
    tr.append(td);
    table.append(tr);
  }
  const scroll = el("div", { className: "admin-table-scroll" });
  scroll.append(table);
  wrap.append(scroll);
  return wrap;
}

export function renderOnboardingSection(root: HTMLElement): void {
  const section = el("div");
  root.append(section);
  const load = () => {
    section.innerHTML = "";
    section.append(el("h2", {}, "Access requests"), el("p", { className: "caption" }, "Loading…"));
    getJson<Overview>("/api/admin/onboarding")
      .then((ov) => {
        section.innerHTML = "";
        section.append(el("h2", {}, "Access requests"));
        const pending = ov.requests.filter((r) => r.status === "pending");
        if (pending.length === 0) section.append(el("p", { className: "caption" }, "No pending requests."));
        for (const r of pending) section.append(requestCard(ov, r, load));
        const decided = ov.requests.filter((r) => r.status !== "pending").slice(0, 10);
        if (decided.length) {
          const d = el("details");
          d.append(el("summary", { className: "caption" }, "Recently decided"));
          const ul = el("ul", { className: "caption" });
          for (const r of decided) ul.append(el("li", {}, `${r.display_name} (${r.username}, ${r.organisation}): ${r.status}`));
          d.append(ul);
          section.append(d);
        }
        section.append(el("h2", {}, "Invite links"), inviteForm(ov, load));
        const tableHolder = el("div");
        tableHolder.append(inviteTable(ov, load));
        section.append(tableHolder);
        const onChanged = () => {
          void getJson<Overview>("/api/admin/onboarding").then((fresh) => {
            tableHolder.innerHTML = "";
            tableHolder.append(inviteTable(fresh, load));
          });
        };
        document.removeEventListener("onb-invites-changed", currentListener);
        currentListener = onChanged;
        document.addEventListener("onb-invites-changed", currentListener);
      })
      .catch(() => {
        section.innerHTML = "";
        section.append(el("h2", {}, "Access requests"), el("p", { className: "caption" }, "Could not load access requests."));
      });
  };
  load();
}

let currentListener: () => void = () => undefined;
